import { TFunction } from "i18next";
import { checkLabel } from "../models/checkLabels";
import { FleetCheckRow, REPORTED_CHECKS } from "../models/fleetCheckRow";
import { describeCount } from "./describeCount";
import { formatOdometerNumber } from "@/pages/live-data/utilities/checklistValues";
import { exportFileName } from "@/utilities/exportFileName";

/** What the sheet has to record about the query itself, so an archived file stands on its own. */
export interface FleetCheckExportContext {
  database: string;
  /** Who ran the check. */
  user: string;
  /** The window the scan covered, as frozen when it started. */
  from: Date;
  to: Date;
  /** When the file was written, which is not when the scan ran. */
  exportedAt: Date;
  language: string;
}

/**
 * The fleet-wide check as a sheet.
 *
 * The query is recorded above the table, not just the result: the same fleet checked over a
 * different window gives different answers, so a file that does not say which window it covered
 * cannot be read a month later. Below that the vehicles form a plain table, one row each.
 */
export function buildFleetCheckExportRows(
  rows: FleetCheckRow[],
  context: FleetCheckExportContext,
  t: TFunction,
): string[][] {
  const when = (date: Date) => date.toLocaleString(context.language);
  const unitInHeader = {
    raw: commonUnit(rows.map(row => row.rawOdometerUnit)) !== "",
    odometer: commonUnit(rows.map(row => row.odometerUnit)) !== "",
  };

  return [
    [t("vehicleDataCheck.export.database", "Database"), context.database || "-"],
    [t("vehicleDataCheck.export.user", "Checked by"), context.user || "-"],
    [
      t("vehicleDataCheck.export.timeRange", "Window checked"),
      `${when(context.from)} – ${when(context.to)}`,
    ],
    [t("vehicleDataCheck.export.queryTime", "Checked at"), when(context.to)],
    [t("vehicleDataCheck.export.exportedAt", "Exported at"), when(context.exportedAt)],
    [],
    headerRow(rows, t),
    ...rows.map(row => bodyRow(row, t, context.language, unitInHeader)),
  ];
}

/**
 * The unit a whole column of odometers is in, or nothing when its rows disagree.
 *
 * The unit comes from the user's measurement profile, so in practice every row of one column
 * shares it — and then it belongs in the heading, where it does not stop the column from being a
 * column of numbers. Where they genuinely differ there is no honest heading, and the unit stays
 * in the cells.
 */
function commonUnit(units: string[]): string {
  const present = units.filter(unit => unit !== "");
  return present.length > 0 && present.every(unit => unit === present[0]) ? present[0] : "";
}

function withUnit(label: string, unit: string): string {
  return unit ? `${label} (${unit})` : label;
}

function headerRow(rows: FleetCheckRow[], t: TFunction): string[] {
  return [
    t("vehicleDataCheck.columns.asset", "Asset"),
    t("vehicleDataCheck.communicating", "Communicating"),
    t("liveData.checklist.licensePlate", "License plate"),
    t("vehicleDataCheck.columns.serial", "Serial number"),
    t("vin", "VIN"),
    t("vehicleDataCheck.columns.vinDecoded", "Vehicle type (VIN)"),
    t("vehicleDataCheck.columns.lastRecordAt", "Last record"),
    withUnit(
      t("liveData.checklist.rawOdometer", "Raw odometer"),
      commonUnit(rows.map(row => row.rawOdometerUnit)),
    ),
    withUnit(
      t("liveData.checklist.odometer", "Odometer"),
      commonUnit(rows.map(row => row.odometerUnit)),
    ),
    ...REPORTED_CHECKS.map(check => checkLabel(check, t)),
    t("liveData.checklist.firmware", "Firmware"),
    t("vehicleDataCheck.columns.harness", "Harness"),
    t("liveData.checklist.fmsStandardSoftwareVersion", "Generic FMS standard software version"),
    t("vehicleDataCheck.columns.canProtocols", "CAN protocols"),
    t("liveData.checklist.ioxDevices", "IOX devices"),
    t("vehicleDataCheck.columns.status", "Status"),
  ];
}

function bodyRow(
  row: FleetCheckRow,
  t: TFunction,
  language: string,
  unitInHeader: { raw: boolean; odometer: boolean },
): string[] {
  const yes = t("common.yes", "Yes");
  const no = t("common.no", "No");
  const missing = "-";

  return [
    row.name,
    communicatingLabel(row.isCommunicating, t),
    row.licensePlate || missing,
    row.serial || missing,
    row.vin || missing,
    row.vinDecoded || missing,
    row.lastRecordAt ? row.lastRecordAt.toLocaleString(language) : missing,
    // The unit is left out where the heading already carries it, so the column stays a column of
    // numbers -- with "684.345,4 km" in the cell nothing downstream can add the values up.
    formatOdometerNumber(row.rawOdometer, unitInHeader.raw ? "" : row.rawOdometerUnit, language),
    formatOdometerNumber(row.odometer, unitInHeader.odometer ? "" : row.odometerUnit, language),
    ...REPORTED_CHECKS.map(check => (row.reported[check] ? yes : no)),
    row.firmware || missing,
    row.harness ? t(`vehicleDataCheck.harness.${row.harness}`, row.harness) : missing,
    row.fmsStandardSoftwareVersion || missing,
    describeCount(row.canProtocols),
    describeCount(row.ioxDevices),
    t(`vehicleDataCheck.status.${row.status}`, row.status),
  ];
}

function communicatingLabel(isCommunicating: boolean | undefined, t: TFunction): string {
  if (isCommunicating === undefined) {
    return t("vehicleDataCheck.notKnown", "not known");
  }
  return isCommunicating ? t("common.yes", "Yes") : t("common.no", "No");
}

/** A file name that says which database was checked and when. */
export function fleetCheckFileName(database: string, exportedAt: Date): string {
  return exportFileName("vehicle-data-check", database, exportedAt);
}
